<template>
  <div class="product-price-input">
    <v-row>
      <v-col :cols="threeRows ? 12 : 4" :class="{ 'pb-0': priceHint !== '' && errorMessages.length === 0 && threeRows }">
        <v-text-field
          v-model="inputPrice"
          label="Pris"
          type="number"
          outlined
          :dense="dense"
          :autofocus="autofocus"
          :error-messages="errorMessages"
          :hint="priceHint"
          :persistent-hint="priceHint !== ''"
          hide-spin-buttons
          :hide-details="hideDetails"
        ></v-text-field>
      </v-col>
      <v-col :cols="threeRows ? 12 : 5">
        <v-select
          :items="discountTypeOptions"
          v-model="discountSelection"
          label="Rabatt"
          :dense="dense"
          :loading="isLoadingDiscountPresets"
          outlined
          hide-details
        ></v-select>
      </v-col>
      <v-col v-if="showDiscountValueInput" :cols="threeRows ? 12 : 3">
        <v-text-field
          :label="discountValueLabel"
          v-model="discountValue"
          :suffix="discountValueSuffix"
          type="number"
          outlined
          :dense="dense"
          hide-details
          hide-spin-buttons
        ></v-text-field>
      </v-col>
    </v-row>
    <div v-if="charge.discount_type && charge.discount_name && !selectedDiscountPreset" class="text-caption mb-2">{{ charge.discount_name }}</div>
  </div>
</template>

<script>
import DiscountPresetService from '@/services/DiscountPresetService'

export default {
  name: 'ProductPriceInput',
  props: {
    value: Object,
    allowDiscountPresets: Boolean,
    errorMessages: Array,
    threeRows: Boolean,
    dense: Boolean,
    autofocus: Boolean,
    hideDetails: {
      type: [Boolean, String],
      default: 'auto'
    }
  },
  data: () => ({
    discountTypes: [
      {
        text: 'Ingen rabatt',
        value: null
      },
      {
        text: 'Nytt pris',
        value: 'fixed'
      },
      {
        text: 'Prisnedsättning',
        value: 'flat'
      },
      {
        text: 'Procentuell',
        value: 'percent'
      }
    ],
    discountPresets: [],
    isLoadingDiscountPresets: false
  }),
  computed: {
    charge: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    inputPrice: {
      get () {
        if (this.charge.price === '' || this.charge.price === null || this.charge.price === undefined) {
          return ''
        }
        return this.charge.price / 100
      },
      set (val) {
        this.$set(this.charge, 'discount_name', null)
        console.log('inputPrice set', val)
        const price = Math.round(val * 100)
        if (this.charge.product?.price && price !== this.charge.original_price) {
          // Ändrar priset direkt i prisfältet på en produkt med fast pris, tolkas som att de vill rabattera produkten
          if (price === 0) {
            // Om nya priset är 0 så sätts procentuell rabatt till 100% (mest för att det ser snyggare ut med "100% rabatt" än "Nytt pris: 0kr")
            this.discountType = 'percent'
            this.discountValue = 100
          } else {
            // Annars så sätts "nytt pris" rabatt
            this.discountType = 'fixed'
            this.discountValue = this.charge.original_price - price
          }
        } else {
          this.charge.original_price = price // Om produkten inte har ett pris så sätts original_price till det nya priset (det kan läggas till rabatter på produkter utan pris och då behöver det angivna priset vara original_price)
          this.discountType = null // Tar bort rabatt om ändrar priset till samma som original_price
        }
        this.price = price
      }
    },
    price: {
      get () {
        return this.charge.price
      },
      set (val) {
        this.charge.price = Math.round(val)
        this.$emit('updated-price')
      }
    },
    discountValue: {
      get () {
        if (this.charge.discount_value === null) {
          return null
        }
        return this.charge.discount_value / 100
      },
      set (val) {
        this.$set(this.charge, 'discount_name', null)
        if (this.discountType === 'flat') {
          this.charge.discount_value = Math.round(val * 100)
          this.price = this.charge.original_price - this.charge.discount_value
        } else if (this.discountType === 'percent') {
          this.charge.discount_value = Math.round(val * 100)
          this.price = this.charge.original_price * (1 - (this.charge.discount_value / 10000))
        } else if (this.discountType === 'fixed') {
          //
        } else {
          // Ingen rabatt
          this.charge.discount_value = null
          this.price = this.charge.original_price
        }
      }
    },
    discountType: {
      get () {
        return this.charge.discount_type
      },
      set (val) {
        this.$set(this.charge, 'discount_name', null)
        this.charge.discount_type = val
        if (['flat', 'percent'].includes(val)) {
          this.discountValue = 0
        } else {
          this.discountValue = null
        }

        // Återställer ordinarie pris när ändrar discount type
        this.price = this.charge.original_price
      }
    },
    selectedDiscountPreset: function () {
      return this.discountPresets.find(preset => (
        this.charge.discount_type === 'percent' &&
        this.charge.discount_name === preset.name &&
        this.charge.discount_value === Number(preset.percent)
      ))
    },
    discountSelection: {
      get () {
        return this.selectedDiscountPreset ? `preset:${this.selectedDiscountPreset.id}` : this.discountType
      },
      set (val) {
        if (typeof val === 'string' && val.startsWith('preset:')) {
          const presetId = val.slice('preset:'.length)
          const preset = this.discountPresets.find(preset => String(preset.id) === presetId)
          if (preset) {
            this.selectPreset(preset)
          }
          return
        }
        this.discountType = val
      }
    },
    discountTypeOptions: function () {
      if (!this.allowDiscountPresets || this.discountPresets.length === 0) {
        return this.discountTypes
      }
      return [
        ...this.discountTypes,
        { divider: true },
        { header: 'Förinställda rabatter' },
        ...this.discountPresets.map(preset => ({
          text: `${preset.name} ${preset.percent / 100}%`,
          value: `preset:${preset.id}`
        }))
      ]
    },
    priceHint: function () {
      if (['flat', 'percent', 'fixed'].includes(this.discountType)) {
        let price = 0
        if (this.charge.original_price !== 0 && this.charge.original_price !== null) {
          price = this.charge.original_price
        }
        return `Ordinarie pris: ${window.conversion.toHumanReadableAmount(price / 100)}`
      }
      return ''
    },
    showDiscountValueInput: function () {
      return ['flat', 'percent'].includes(this.discountType)
    },
    discountValueLabel: function () {
      if (this.discountType === 'flat') {
        return 'Rabatt i kronor'
      } else if (this.discountType === 'percent') {
        return 'Rabatt i procent'
      } else {
        return ''
      }
    },
    discountValueSuffix: function () {
      if (this.discountType === 'flat' || this.discountType === 'fixed') {
        return 'Kr'
      } else {
        return '%'
      }
    }
  },
  methods: {
    async loadDiscountPresets () {
      this.isLoadingDiscountPresets = true
      try {
        const { data } = await DiscountPresetService.getAll()
        if (data.status !== 'success') { throw new Error() }
        this.discountPresets = data.data.discount_presets
      } catch {
        this.discountPresets = []
      } finally {
        this.isLoadingDiscountPresets = false
      }
    },
    selectPreset (preset) {
      this.discountType = 'percent'
      this.discountValue = preset.percent / 100
      this.$set(this.charge, 'discount_name', preset.name)
    }
  },
  watch: {
    allowDiscountPresets: {
      immediate: true,
      handler (isAllowed) {
        if (isAllowed) {
          this.loadDiscountPresets()
        } else {
          this.discountPresets = []
        }
      }
    }
  }
}
</script>
